import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import PageSEO from '../components/PageSEO';

export const ProcessPage = () => {
  const steps = [
    {
      title: "Ön Değerlendirme",
      short: "Analiz ve Keşif",
      description: "Yapınızın mevcut durumunu analiz ediyor, potansiyelini değerlendiriyor ve size özel bir fizibilite raporu hazırlıyoruz.",
      details: "Uzman ekibimiz projenizi yerinde inceler, mevcut yapı durumunu değerlendirir ve detaylı bir analiz raporu hazırlar. Bu aşamada potansiyel değer artışı, süreç planlaması ve maliyet projeksiyonu konusunda şeffaf bilgilendirme yapılır.",
    },
    {
      title: "Fizibilite ve Planlama",
      short: "Strateji ve Tasarım",
      description: "İhtiyaçlarınız doğrultusunda mimari proje tasarlanır, mühendislik hesapları yapılır ve detaylı planlama hazırlanır.",
      details: "İhtiyaçlarınız ve beklentileriniz doğrultusunda mimari proje tasarlanır. Mühendislik hesapları yapılır, 3D görselleştirmeler hazırlanır. Maliyet analizi ve zaman planlaması netleştirilir.",
    },
    {
      title: "Tasarım ve Onay",
      short: "Hukuki Süreç",
      description: "Şeffaf sözleşme süreci ve gerekli tüm resmi izinlerin alınması. Her aşamada hukuki güvence sağlanır.",
      details: "Tüm paydaşlarla şeffaf ve adil bir sözleşme süreci yürütülür. İlgili kurumlara başvurular yapılır, ruhsat ve izinler alınır. Tüm yasal süreçler titizlikle takip edilir.",
    },
    {
      title: "Uygulama ve İnşaat",
      short: "Mühendislik ve Yapım",
      description: "En yüksek kalite standartlarında, sürekli denetim altında inşaat uygulaması. İş güvenliği önceliğimizdir.",
      details: "En yüksek kalite standartlarında, sürekli denetim altında inşaat başlar. Düzenli ilerleme raporları ve görsel güncellemeler ile süreci şeffaf bir şekilde takip edebilirsiniz.",
    },
    {
      title: "Teslim ve Değer",
      short: "Anahtar Teslim",
      description: "Anahtar teslim yeni yaşam alanınız ve artırılmış mülk değeriniz. Teslim sonrası destek garantimiz devam eder.",
      details: "İnşaat tamamlandığında anahtar teslim yeni yaşam alanınızı teslim alırsınız. İskan, tapu ve tüm resmi işlemler tarafımızdan takip edilir. Teslim sonrası destek ve garanti süreçlerimiz devam eder.",
    },
  ];

  const faqs = [
    {
      q: "Proje süreci ne kadar sürer?",
      a: "Proje ölçeğine göre değişmekle birlikte, ortalama bir proje 18-24 ay sürmektedir. Bu süre, planlama, izin süreçleri ve inşaat aşamalarını kapsar."
    },
    {
      q: "İnşaat süresince nerede kalacağım?",
      a: "Kentsel dönüşüm projelerinde, hak sahiplerimize inşaat süresince kira yardımı veya geçici konaklama desteği sağlıyoruz."
    },
    {
      q: "Mülk değerim ne kadar artacak?",
      a: "Konum, proje kalitesi ve piyasa koşullarına bağlı olarak projelerimiz ortalama %40-80 arasında değer artışı sağlayabilmektedir."
    },
    {
      q: "Kalite garantisi var mı?",
      a: "Tüm projelerimizde yapı güvenliği ve işçilik kalitesi konusunda garanti sunuyoruz. Teslim sonrası destek ve bakım hizmetlerimiz sözleşmede belirtilen süre boyunca devam eder."
    },
  ];

  return (
    <div data-testid="process-page" className="page-transition">
      <PageSEO
        title="Süreç"
        description="VE PERA İnşaat çalışma süreci — Ön değerlendirmeden anahtar teslime kadar şeffaf, güvenli ve profesyonel 5 adımlık sürecimiz."
        path="/surec"
      />
      {/* Hero - Editorial */}
      <section className="pt-32 lg:pt-40 pb-20 bg-[#f2f2f7]">
        <div className="container-wide">
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <span className="overline mb-6 block">Sürecimiz</span>
              <h1
                className="text-[clamp(2.5rem,6vw,4.5rem)] leading-[1] tracking-[-0.03em]"
                style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
              >
                Birlikte yürüdüğümüz
                <br />
                <span className="italic font-light text-[#8e8e93]">güvenli</span> yol
              </h1>
            </div>
            <div className="lg:col-span-4 lg:col-start-9 flex items-end">
              <p
                className="text-[#8e8e93] leading-[1.9] text-[15px]"
                style={{ fontFamily: "var(--font-body)" }}
              >
                İlk değerlendirmeden anahtar teslimine kadar şeffaf, güvenli ve profesyonel bir süreç. Her adımda yanınızdayız.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps - Horizontal editorial lines */}
      <section data-testid="process-steps" className="section-padding bg-[#f2f2f7]">
        <div className="container-wide">
          <div className="border-t border-[#d1d1d6]">
            {steps.map((step, index) => (
              <div
                key={index}
                data-testid={`process-step-${index}`}
                className="grid grid-cols-12 gap-4 lg:gap-8 py-12 lg:py-16 border-b border-[#d1d1d6] group"
              >
                {/* Step indicator */}
                <div className="col-span-12 lg:col-span-1 flex items-start">
                  <span
                    className="text-[10px] tracking-[0.2em] uppercase text-[#041f60]"
                    style={{ fontFamily: "var(--font-body)", fontWeight: 500 }}
                  >
                    Adım {index + 1}
                  </span>
                </div>

                {/* Title */}
                <div className="col-span-12 lg:col-span-3 flex flex-col justify-center">
                  <span
                    className="text-[10px] tracking-[0.2em] uppercase text-[#8e8e93]/50 mb-2 block"
                    style={{ fontFamily: "var(--font-body)", fontWeight: 500 }}
                  >
                    {step.short}
                  </span>
                  <h2
                    className="text-[20px] lg:text-[24px] group-hover:text-[#b87333] transition-colors duration-500"
                    style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
                  >
                    {step.title}
                  </h2>
                </div>

                {/* Description */}
                <div className="col-span-12 lg:col-span-4 lg:col-start-5 mt-4 lg:mt-0 flex items-center">
                  <p
                    className="text-[#1a1a1a]/80 text-[14px] leading-[1.8]"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {step.description}
                  </p>
                </div>

                {/* Detail */}
                <div className="col-span-12 lg:col-span-3 lg:col-start-10 mt-4 lg:mt-0 flex items-center">
                  <p
                    className="text-[#8e8e93]/70 text-[13px] leading-[1.8]"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {step.details}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ - Clean editorial */}
      <section data-testid="faq-section" className="section-padding bg-[#d1d1d6]/20">
        <div className="container-wide">
          <div className="grid lg:grid-cols-12 gap-16">
            {/* Left - Heading */}
            <div className="lg:col-span-4">
              <span className="overline mb-6 block">SSS</span>
              <h2
                className="text-[clamp(2rem,4vw,3rem)] leading-[1.1] tracking-[-0.02em]"
                style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
              >
                Merak edilenler
              </h2>
            </div>

            {/* Right - Questions */}
            <div className="lg:col-span-7 lg:col-start-6">
              <div className="border-t border-[#d1d1d6]">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="py-8 lg:py-10 border-b border-[#d1d1d6]"
                  >
                    <h3
                      className="text-[18px] lg:text-[20px] mb-4"
                      style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
                    >
                      {faq.q}
                    </h3>
                    <p
                      className="text-[#8e8e93] leading-[1.9] text-[14px]"
                      style={{ fontFamily: "var(--font-body)" }}
                    >
                      {faq.a}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-[#1a1a1a]">
        <div className="container-wide">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <h2
                className="text-[clamp(1.75rem,4vw,2.5rem)] leading-[1.15] tracking-[-0.02em] text-[#f2f2f7]"
                style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
              >
                Sürecinizi başlatmaya hazır mısınız?
              </h2>
            </div>
            <div className="lg:col-span-4 lg:col-start-9 flex lg:justify-end">
              <Link to="/iletisim" className="btn-hero-primary">
                Ücretsiz Ön Değerlendirme
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProcessPage;
